import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DropResult,
  Droppable,
  type DroppableProps,
} from "@hello-pangea/dnd";
import React, {
  type CSSProperties,
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useState,
} from "react";

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export const getItemStyle = (
  provided?: DraggableProvided,
  snapshot?: DraggableStateSnapshot,
  lockAxis?: "x" | "y",
) => {
  let { transform } = provided?.draggableProps.style || {};

  if (snapshot?.isDragging && transform && lockAxis === "y") {
    transform = transform.replace(/\(.+,/, "(0,");
  }
  if (snapshot?.isDragging && transform && lockAxis === "x") {
    transform = transform.replace(/,.+\)/, ",0)");
  }

  return {
    userSelect: "none" as CSSProperties["userSelect"],
    ...provided?.draggableProps.style,
    transform,
  };
};

const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

function useIndices<T extends { countIndex?: boolean }>(items: T[]) {
  const [indices, setIndices] = useState<(number | undefined)[]>([]);
  const update = (items: T[]) => {
    let count = 0;
    const updatedTempIndices = items.map((item: T) => {
      if (item.countIndex === false) {
        return undefined;
      }
      return count++;
    });
    setIndices(updatedTempIndices);
  };

  useEffect(() => {
    update(items);
  }, [items]);

  return [indices, update] as const;
}
interface ItemInterface<T> {
  item?: T;
  temporaryIndex?: number;
}

interface SortableProps<T extends Record<string, any>> {
  SortableItem: FC<ItemInterface<T> & Record<string, any>>;
  items: T[];
  itemProps?: Record<string, any>;
  lockAxis: "x" | "y";
  setItems: Dispatch<SetStateAction<T[]>> | ((items: T[]) => void);
  disabled?: boolean;
  direction?: DroppableProps["direction"];
}

export function useOnDragEnd<T extends Record<string, any>>(
  items: T[],
  setItems: SortableProps<T>["setItems"],
  updateIndices?: (items: T[]) => void,
) {
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const updatedItems = reorder(
      items,
      result.source.index,
      result.destination.index,
    );
    setItems(updatedItems);
    updateIndices?.(updatedItems);
  };
  return onDragEnd;
}
const Sortable: FC<SortableProps<any>> = ({
  SortableItem,
  setItems,
  lockAxis,
  items,
  itemProps,
  direction = "vertical",
  ...props
}) => {
  const [tempIndices, updateIndices] = useIndices(items);
  const onDragEnd = useOnDragEnd(items, setItems, updateIndices);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable droppableId="list" direction={direction}>
        {(provided) => (
          <div
            {...provided.droppableProps}
            style={{
              display: "flex",
              flexDirection: direction === "vertical" ? "column" : "row",
            }}
            ref={provided.innerRef}
          >
            {items?.map((item, index) => (
              <Draggable
                key={item.uid.toString()}
                draggableId={item.uid.toString()}
                index={index}
                isDragDisabled={props.disabled || item.disabled}
              >
                {(provided, snapshot) => (
                  <div ref={provided.innerRef}>
                    <SortableItem
                      {...props}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      {...itemProps}
                      item={item}
                      temporaryIndex={tempIndices[index]}
                      tabIndex={-1}
                      style={getItemStyle(provided, snapshot, lockAxis)}
                    >
                      {item.name}
                    </SortableItem>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

export default Sortable;
